import { useState, type ReactNode } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { isConfigured } from './lib/firebase';
import { Icon } from './components/Icons';
import { Loading, OverlayRoot } from './components/ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import SignIn from './screens/SignIn';
import Setup from './screens/Setup';
import Trips from './screens/Trips';
import Trip from './screens/Trip';
import ExpenseDetail from './screens/ExpenseDetail';
import Activity from './screens/Activity';
import You from './screens/You';
import Join from './screens/Join';

const ROOT_TABS = ['/', '/activity', '/you'];

function TabBar() {
  const tabs = [
    { to: '/', label: 'Trips', icon: 'trips' },
    { to: '/activity', label: 'Activity', icon: 'activityFeed' },
    { to: '/you', label: 'You', icon: 'you' }
  ];
  return (
    <nav className="tabbar">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.to === '/'}>
          <Icon name={t.icon} />
          <span>{t.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (!isConfigured) return <Frame><Setup /></Frame>;
  if (loading) return <Frame><Loading label="Signing you in…" /></Frame>;

  if (!user) {
    // Keep an invite link intact through sign-in so the link still works.
    return (
      <Frame>
        <SignIn pendingInvite={location.pathname.startsWith('/join/') ? location.pathname : null} />
      </Frame>
    );
  }

  return (
    <Frame tabs={ROOT_TABS.includes(location.pathname)}>
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/" element={<Trips />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/you" element={<You />} />
          <Route path="/join/:code" element={<Join />} />
          <Route path="/t/:groupId" element={<Trip tab="expenses" />} />
          <Route path="/t/:groupId/balances" element={<Trip tab="balances" />} />
          <Route path="/t/:groupId/families" element={<Trip tab="families" />} />
          <Route path="/t/:groupId/e/:expenseId" element={<ExpenseDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </Frame>
  );
}

function Frame({ children, tabs }: { children: ReactNode; tabs?: boolean }) {
  // Sheets portal into the frame so they layer above the floating button.
  const [phone, setPhone] = useState<HTMLDivElement | null>(null);
  return (
    <div className="stage">
      <div className="phone" ref={setPhone}>
        <OverlayRoot.Provider value={phone}>
          {children}
          {tabs && <TabBar />}
        </OverlayRoot.Provider>
      </div>
    </div>
  );
}
